import {
    AfterViewInit,
    Component,
    ElementRef,
    OnDestroy,
    OnInit
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { ContactRequest, Office, SiteApiService } from '../../core/services/api.service';

declare var L: any;

interface ContactOfficeLocation {
    no: string;
    country: string;
    city: string;
    address: string;
    phoneLabel: string;
    phone: string;
    secondPhoneLabel?: string;
    secondPhone?: string;
    email: string;
    lat: number;
    lng: number;
}

@Component({
    selector: 'app-contact',
    templateUrl: './contact.component.html'
})
export class ContactComponent implements OnInit, AfterViewInit, OnDestroy {

    offices: Office[] = [];

    officeLocations: ContactOfficeLocation[] = [
        {
            no: '01',
            country: 'UK',
            city: 'Eastleigh',
            address: 'Suit 9, Woodside House, Woodside Avenue, Eastleigh, SO50 9NA United Kingdom',
            phoneLabel: 'Tel.',
            phone: '+44(0) 23 8065 0900',
            secondPhoneLabel: 'Fax',
            secondPhone: '+44(0) 23 8064 2556',
            email: 'sales@iosc.co.uk',
            lat: 50.967,
            lng: -1.352
        },
        {
            no: '02',
            country: 'Qatar',
            city: 'Doha',
            address: 'Zone 24, Street 220, Building 149 Doha, Qatar',
            phoneLabel: 'Tel.',
            phone: '+974 6640 7679 / +974 5207 1774',
            email: 'jasadov@iosc.qa / rrahimov@iosc.qa',
            lat: 25.2684924,
            lng: 51.5228928
        },
        {
            no: '03',
            country: 'Azerbaijan',
            city: 'Baku / Garadagh',
            address: 'Salyan Highway, 32 km, Garadagh district, AZ1083',
            phoneLabel: 'Mob.',
            phone: '+99 (450) 22420 57 / +99 (450) 2242 58',
            email: 'office@iosc.az / sales@iosc.az',
            lat: 40.235,
            lng: 49.556
        },
        {
            no: '04',
            country: 'Georgia',
            city: 'Tbilisi',
            address: 'Vake-saburtalo district, Sairme Hill N 31 Tbilisi Georgia',
            phoneLabel: 'Tel.',
            phone: '+99 (559) 1069761 / +99 (450) 2242057',
            email: 'ali.rajabov@iosc.ge',
            lat: 41.725,
            lng: 44.745
        },
        {
            no: '05',
            country: 'Kazakhstan',
            city: 'Almaty',
            address: '498 Seifullin str. BC Altyn Zaman office 305 Almaty 050012 Kazakhstan',
            phoneLabel: 'Tel./Fax',
            phone: '+7 (727) 9738470',
            secondPhoneLabel: 'Mob.',
            secondPhone: '+77750000220',
            email: 'office@ioscca.kz',
            lat: 43.238,
            lng: 76.945
        }
       
    ];

    model: ContactRequest = {
        fullName: '',
        company: '',
        email: '',
        phone: '',
        serviceCategory: 'Industrial Supply',
        priority: 'Normal',
        message: ''
    };

    loadingOffices = false;
    sending = false;
    successMessage = '';
    errorMessage = '';
    selectedOffice = '';

    private observer: IntersectionObserver | null = null;
    private map: any = null;

    constructor(
        private siteApi: SiteApiService,
        private elementRef: ElementRef,
        private route: ActivatedRoute
    ) { }

    ngOnInit(): void {
        this.route.queryParamMap.subscribe(params => {
            this.selectedOffice = params.get('office') || '';

            setTimeout(() => {
                this.scrollToSelectedOffice();
            }, 250);
        });

        this.getOffices();
    }

    isSelectedOffice(country: string): boolean {
        return !!this.selectedOffice && country.toLowerCase() === this.selectedOffice.toLowerCase();
    }

    private scrollToSelectedOffice(): void {
        if (!this.selectedOffice) {
            return;
        }

        const selectedCard = this.elementRef.nativeElement.querySelector('.contact-office-detail-card.selected') as HTMLElement;

        if (!selectedCard) {
            return;
        }

        selectedCard.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
        });
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.initRevealAnimation();
            this.initContactMap();
        }, 350);
    }

    ngOnDestroy(): void {
        if (this.observer) {
            this.observer.disconnect();
        }

        if (this.map) {
            this.map.remove();
            this.map = null;
        }
    }

    getOffices(): void {
        this.loadingOffices = true;

        this.siteApi.getOffices().subscribe(
            (data: Office[]) => {
                this.offices = data || [];
                this.loadingOffices = false;

                setTimeout(() => {
                    this.initRevealAnimation();
                    this.initContactMap();
                }, 200);
            },
            (error) => {
                console.error(error);
                this.loadingOffices = false;

                setTimeout(() => {
                    this.initContactMap();
                }, 200);
            }
        );
    }

    submit(): void {
        this.successMessage = '';
        this.errorMessage = '';

        if (!this.model.fullName || !this.model.email || !this.model.message) {
            this.errorMessage = 'Please fill required fields.';
            return;
        }

        this.sending = true;

        this.siteApi.sendContactRequest(this.model).subscribe(
            (res) => {
                this.sending = false;
                this.successMessage = res.message || 'Your request has been submitted successfully.';

                this.model = {
                    fullName: '',
                    company: '',
                    email: '',
                    phone: '',
                    serviceCategory: 'Industrial Supply',
                    priority: 'Normal',
                    message: ''
                };
            },
            (error) => {
                console.error(error);
                this.sending = false;
                this.errorMessage = 'Request could not be submitted.';
            }
        );
    }

    private initRevealAnimation(): void {
        const elements = this.elementRef.nativeElement.querySelectorAll('.reveal');

        if (!elements || elements.length === 0) {
            return;
        }

        if (this.observer) {
            this.observer.disconnect();
        }

        this.observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const el = entry.target as HTMLElement;
                        el.classList.add('show');
                        this.observer?.unobserve(el);
                    }
                });
            },
            {
                threshold: 0.14
            }
        );

        elements.forEach((el: HTMLElement) => {
            el.classList.remove('show');
            this.observer?.observe(el);
        });
    }

    private initContactMap(): void {
        const mapElement = document.getElementById('ioscContactMap');

        if (!mapElement || typeof L === 'undefined') {
            return;
        }

        if (this.map) {
            this.map.remove();
            this.map = null;
        }

        this.map = L.map('ioscContactMap', {
            scrollWheelZoom: false
        }).setView([42.5, 45.5], 4);

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; OpenStreetMap contributors'
        }).addTo(this.map);

        const markers: any[] = [];

        this.officeLocations.forEach(o => {
            const marker = L.marker([o.lat, o.lng]).addTo(this.map);

            let popup =
                '<b>' + o.country + '</b><br>' +
                o.address + '<br>' +
                o.phoneLabel + ': ' + o.phone + '<br>';

            if (o.secondPhoneLabel && o.secondPhone) {
                popup += o.secondPhoneLabel + ': ' + o.secondPhone + '<br>';
            }

            popup += 'E-mail: ' + o.email;

            marker.bindPopup(popup);
            markers.push(marker);
        });

        const group = L.featureGroup(markers);
        this.map.fitBounds(group.getBounds().pad(0.22));

        setTimeout(() => {
            this.map.invalidateSize();
        }, 300);
    }
}

